import * as EmailValidator from 'email-validator'
import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import * as Sentry from '@sentry/react'

import { Button } from '@/components/Button/Button'
import { LoadingIcon } from '@/components/LoadingIcon'
import { TextInput } from '@/components/widget-components'
import { usePasswordlessAuth } from '@/hooks/usePasswordlessAuth'
import { usePageStateStore } from '@/reducers'
import { track } from '@/utils/analytics'
import { PanelLayout } from '@/panels/login/PanelLayout'
import { ROUTES } from '@/panels/corporatesignup/CorporateSignupPanel'

const CorporateLoginPanelInner = () => {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [inputError, setInputError] = useState('')

  const setAuthToken = usePageStateStore.use.setAuthToken()
  const setLoggedInUser = usePageStateStore.use.setEmail()
  const navigate = useNavigate()

  const errorMessages = useMemo(
    () => ({
      sendFailed: t('login.errors.unableToLogin'),
      timedOut: t('login.errors.signInTimedOut'),
      unexpected: t('login.errors.unexpectedError'),
    }),
    [t]
  )

  const onVerified = useCallback(
    ({
      email: verifiedEmail,
      accessToken,
    }: {
      email: string
      accessToken: string
    }) => {
      setAuthToken(accessToken)
      setLoggedInUser(verifiedEmail)
      navigate(ROUTES.CORPORATE_INFO, { replace: true })
    },
    [navigate, setAuthToken, setLoggedInUser]
  )

  const {
    sendMagicLink,
    waitingForApproval,
    denied,
    loading,
    errorMessage,
    resetDenied,
  } = usePasswordlessAuth({
    client: 'widget',
    sentryComponent: 'CorporateLoginPanel',
    errorMessages,
    onVerified,
  })

  const triggerLogin = useCallback(async () => {
    if (!email?.length || !EmailValidator.validate(email)) {
      setInputError(t('login.errors.invalidEmail'))
      return
    }
    setInputError('')
    Sentry.setUser({ email })
    track('corporate_login_email_submitted')
    await sendMagicLink({ email })
  }, [email, sendMagicLink, t])

  const onEmailChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) =>
      setEmail(event.target.value),
    []
  )

  const displayedError = inputError || errorMessage

  if (denied) {
    return (
      <PanelLayout titleKey="welcome.continueTitle">
        <div className="flex flex-col gap-5">
          <h2
            data-testid="text-corporate-login-denied-title"
            className="text-lg font-normal uppercase"
          >
            {t('login.denied.title')}
          </h2>
          <div className="text-base">{t('login.denied.status')}</div>
          <Button
            onClick={resetDenied}
            data-testid="btn-corporate-login-denied-try-again"
            className="inline-flex items-center py-8 px-4 w-full justify-center mt-5"
          >
            {t('login.denied.tryAgain')}
          </Button>
        </div>
      </PanelLayout>
    )
  }

  if (waitingForApproval) {
    return (
      <PanelLayout titleKey="welcome.continueTitle">
        <div className="flex flex-col gap-5">
          <h2
            data-testid="text-corporate-login-check-email-title"
            className="text-lg font-normal uppercase"
          >
            {t('login.checkEmail.title')}
          </h2>
          <div className="text-base" data-testid="text-corporate-check-email">
            {t('login.checkEmail.sentLink', { email })}
          </div>
          <div
            className="text-base"
            data-testid="text-corporate-keep-page-open"
          >
            {t('login.checkEmail.keepPageOpen')}
          </div>
          <LoadingIcon />
        </div>
      </PanelLayout>
    )
  }

  return (
    <PanelLayout titleKey="welcome.continueTitle">
      <div className="flex flex-col gap-5">
        <h2
          data-testid="text-corporate-login-title"
          className="text-lg font-normal uppercase"
        >
          {t('corporateLogin.title')}
        </h2>
        <div data-testid="text-corporate-login-subtitle" className="text-base">
          {t('corporateLogin.subtitle')}
        </div>
        <TextInput
          autoFocus
          data-testid="input-corporate-login-email"
          type="email"
          value={email}
          onChange={onEmailChange}
          onKeyDown={(event) => {
            if (event.key === 'Enter') triggerLogin()
          }}
          placeholder={t('corporateLogin.emailPlaceholder')}
        />
        <div
          data-testid="text-corporate-login-email-disclaimer"
          className="text-sm opacity-80"
        >
          {t('corporateLogin.emailDisclaimer')}
        </div>
        <div
          data-testid="text-corporate-login-no-account"
          className="text-sm opacity-80"
        >
          {t('corporateLogin.noAccount')}
        </div>
        {displayedError && (
          <div
            className="my-1 text-rose-600"
            data-testid="error-corporate-login"
          >
            {displayedError}
          </div>
        )}
        <Button
          onClick={triggerLogin}
          disabled={loading}
          data-testid="btn-corporate-login-continue"
          className="inline-flex items-center py-8 px-4 w-full justify-center mt-5"
        >
          {loading ? <LoadingIcon /> : t('corporateLogin.continue')}
        </Button>
      </div>
    </PanelLayout>
  )
}

export const CorporateLoginPanel = React.memo(CorporateLoginPanelInner)
